import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ServiceCreateSchema,
  ServiceUpdateSchema,
  type ServiceCreateRequest,
  type ServiceItem,
  type ServiceUpdateRequest,
} from '@crm/shared';
import { extractApiError } from '../../lib/api';
import { useServiceCreate, useServiceUpdate } from './api';

type Props = {
  open: boolean;
  service: ServiceItem | null; // null = tryb create
  onClose: () => void;
};

type FormState = {
  name: string;
  category: string;
  durationMinutes: string;
  price: string;
  description: string;
};

const EMPTY: FormState = {
  name: '',
  category: '',
  durationMinutes: '30',
  price: '0.00',
  description: '',
};

function fromService(s: ServiceItem): FormState {
  return {
    name: s.name,
    category: s.category ?? '',
    durationMinutes: String(s.durationMinutes),
    price: s.price,
    description: s.description ?? '',
  };
}

export function ServiceFormModal({ open, service, onClose }: Props): ReactNode {
  const { t } = useTranslation();
  const isEdit = service !== null;
  const create = useServiceCreate();
  const update = useServiceUpdate(service?.id ?? 0);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSubmitError(null);
    setForm(service ? fromService(service) : EMPTY);
  }, [open, service]);

  if (!open) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const candidate = {
      name: form.name,
      category: form.category.trim() === '' ? null : form.category,
      durationMinutes: Number(form.durationMinutes),
      price: form.price,
      description: form.description.trim() === '' ? null : form.description,
    };

    const schema = isEdit ? ServiceUpdateSchema : ServiceCreateSchema;
    const parsed = schema.safeParse(candidate);
    if (!parsed.success) {
      const fld: Partial<Record<keyof FormState, string>> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (path === 'name' || path === 'category' || path === 'durationMinutes' || path === 'price' || path === 'description') {
          fld[path] = issue.message;
        }
      }
      setErrors(fld);
      return;
    }
    setErrors({});

    try {
      if (isEdit && service) {
        await update.mutateAsync(parsed.data as ServiceUpdateRequest);
      } else {
        await create.mutateAsync(parsed.data as ServiceCreateRequest);
      }
      onClose();
    } catch (err) {
      setSubmitError(extractApiError(err));
    }
  };

  const submitting = create.isPending || update.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-zinc-900">
            {isEdit ? t('services.form.titleEdit') : t('services.form.titleCreate')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
            aria-label="close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Field label={t('services.form.name')} error={errors.name}>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="input"
              required
              autoFocus
            />
          </Field>

          <Field label={t('services.form.category')} error={errors.category}>
            <input
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              className="input"
              placeholder="Fryzjer / Kosmetyka"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('services.form.duration')} error={errors.durationMinutes}>
              <input
                type="number"
                min={5}
                max={480}
                value={form.durationMinutes}
                onChange={(e) => setForm((f) => ({ ...f, durationMinutes: e.target.value }))}
                className="input"
                required
              />
            </Field>
            <Field label={t('services.form.price')} error={errors.price}>
              <input
                value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                className="input"
                placeholder="120.00"
                required
              />
            </Field>
          </div>

          <Field label={t('services.form.description')} error={errors.description}>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              className="input resize-none"
            />
          </Field>

          {submitError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-zinc-400"
            >
              {submitting ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div>
      <label className="block text-sm font-medium text-zinc-700">{label}</label>
      <div className="mt-1">{children}</div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
